import { useMemo, useCallback } from 'react';
import { isSameMonth, isSameYear } from 'date-fns';
import { useEventLocale } from '../context/LocaleContext';
import type { DateOnlyFormat, TimeOnlyFormat, DateTimeFormat, DateRangeFormat, FormatDateOptions, NullableDateInput } from '../formatters/dateFormatter';
import { formatDateForLanguage, formatDateRangeForLanguage } from '../formatters/dateFormatter';
import { useTranslation } from '@shared/core';

type DateInput = Date | number | string | null | undefined;

type FormattedRangeOptions = Readonly<{
  /**
   * Whether to include time formatting (e.g., "2:00 PM to 4:30 PM")
   * If true, displays only the time range without dates
   */
  includeTime?: boolean;
}>;

export type UseEventLocaleFormatterArgs = Readonly<{
  /**
   * Optional language override. If not provided, uses the event's configured locale.
   * Use this for one-off formatting needs in a different locale.
   */
  language?: string;
}>;

export type EventLocaleFormatter = Readonly<{
  /**
   * Format a date using date-only formats (no time component)
   * @param input - Date to format (Date, timestamp, or date string)
   * @param format - Date format string (e.g., 'MMMM dd, yyyy', 'MMMdd')
   * @param options - Optional timezone and titlecase settings
   */
  formatDate: (input: NullableDateInput, format: DateOnlyFormat, options?: FormatDateOptions) => string | undefined;

  /**
   * Format a time using time-only formats (no date component)
   * @param input - Date to format (Date, timestamp, or date string)
   * @param format - Time format string (e.g., 'hh:mm', 'h:mm')
   * @param options - Optional timezone settings
   */
  formatTime: (input: NullableDateInput, format: TimeOnlyFormat, options?: FormatDateOptions) => string | undefined;

  /**
   * Format a datetime using combined date+time formats
   * @param input - Date to format (Date, timestamp, or date string)
   * @param format - DateTime format string (e.g., 'yyyy-MM-dd HH:mm', 'MMMM do, yyyy h:mm')
   * @param options - Optional timezone and titlecase settings
   */
  formatDateTime: (input: NullableDateInput, format: DateTimeFormat, options?: FormatDateOptions) => string | undefined;

  /**
   * Format a date range with smart optimization for same-month/same-year cases
   * @param startDate - Start date (Date, timestamp, or date string)
   * @param endDate - End date (Date, timestamp, or date string)
   * @param options - Optional includeTime flag for time-only ranges
   * @returns Formatted range string (e.g., "Jan 15 - 20, 2025", "2:00 PM to 4:30 PM")
   *
   * @example
   * // Date ranges
   * formatDateRange(start, end) // "Jan 15 - 20, 2025" (same month)
   * formatDateRange(start, end) // "Jan 15 - Feb 20, 2025" (different months)
   * formatDateRange(start, end) // "Dec 20, 2024 - Jan 5, 2025" (different years)
   *
   * // Time ranges
   * formatDateRange(start, end, { includeTime: true }) // "2:00 PM to 4:30 PM"
   */
  formatDateRange: (startDate: DateInput, endDate: DateInput, options?: FormattedRangeOptions) => string | undefined;

  /**
   * Format a date range in a specific EventDetails format token (numeric / ordinal / weekday),
   * locale-aware, with same-month/same-year collapsing. Use for the configurable event display date.
   * @param startDate - Start date (Date, timestamp, or date string)
   * @param endDate - End date (Date, timestamp, or date string); omit/null renders the single start date
   * @param format - One of 'MM/dd/yyyy' | 'MMMM do, yyyy' | 'weekday, dd MMMM yyyy'
   * @param options - Optional timezone and titlecase settings
   *
   * @example
   * formatEventDateRange(start, end, 'MMMM do, yyyy') // "June 12th - 14th, 2026"
   */
  formatEventDateRange: (startDate: NullableDateInput, endDate: NullableDateInput, format: DateRangeFormat, options?: FormatDateOptions) => string | undefined;

  /**
   * The language being used for formatting (BCP 47 tag, e.g., 'en-US', 'fr-FR')
   */
  language: string;
}>;

/**
 * Returns formatter functions that use the event's configured locale.
 * Use this when you need multiple formatting calls with the same locale,
 * or when you need to pass the formatter to a child component.
 *
 * @param args - Optional configuration object with language override
 * @returns Object with formatDate, formatTime, formatDateTime, formatDateRange, and language
 *
 * @example
 * ```tsx
 * // Use event locale
 * const { formatDate, formatTime, formatDateTime, formatDateRange } = useEventLocaleFormatter();
 *
 * // Format a date
 * const eventDate = formatDate(date, 'MMMM do, yyyy');
 *
 * // Format a time
 * const eventTime = formatTime(date, 'hh:mm');
 *
 * // Format a datetime
 * const eventDateTime = formatDateTime(date, 'yyyy-MM-dd HH:mm');
 *
 * // Format a date range
 * const dateRange = formatDateRange(startDate, endDate);
 * const timeRange = formatDateRange(startTime, endTime, { includeTime: true });
 * ```
 *
 * @example
 * ```tsx
 * // Override language for specific formatting needs
 * const { formatDate } = useEventLocaleFormatter({ language: 'fr-FR' });
 * const frenchDate = formatDate(date, 'dd MMMM yyyy'); // "28 août 2025"
 * ```
 */
export const useEventLocaleFormatter = (args: UseEventLocaleFormatterArgs = {}): EventLocaleFormatter => {
  const { language: eventLanguage, hour12 } = useEventLocale();
  const { tq } = useTranslation('dates');

  const language = args.language ?? eventLanguage;

  const formatDate = useCallback(
    (input: NullableDateInput, format: DateOnlyFormat, options?: FormatDateOptions) => formatDateForLanguage(language, input, format, options),
    [language],
  );

  // hour12 first so an explicit per-call option still wins.
  const formatTime = useCallback(
    (input: NullableDateInput, format: TimeOnlyFormat, options?: FormatDateOptions) => formatDateForLanguage(language, input, format, { hour12, ...options }),
    [language, hour12],
  );

  const formatDateTime = useCallback(
    (input: NullableDateInput, format: DateTimeFormat, options?: FormatDateOptions) => formatDateForLanguage(language, input, format, { hour12, ...options }),
    [language, hour12],
  );

  const formatDateRange = useCallback(
    (startDate: DateInput, endDate: DateInput, options: FormattedRangeOptions = {}): string | undefined => {
      const { includeTime = false } = options;

      // For time-only formatting
      if (includeTime) {
        const startTime = formatTime(startDate, 'hh:mm');
        const endTime = formatTime(endDate, 'hh:mm');

        if (!startTime) return undefined;
        if (!endTime) return startTime;
        return tq`${startTime} to ${endTime}`;
      }

      // For date-only formatting
      const startDateObj = startDate instanceof Date ? startDate : startDate ? new Date(typeof startDate === 'number' ? startDate : startDate) : null;
      const endDateObj = endDate instanceof Date ? endDate : endDate ? new Date(typeof endDate === 'number' ? endDate : endDate) : null;

      if (!startDateObj) return '';

      const startMonthDay = formatDate(startDate, 'MMMdd');
      const startYear = formatDate(startDate, 'yyyy');

      // Single date (no end date)
      if (!endDateObj) {
        return `${startMonthDay}, ${startYear}`;
      }

      const endDay = formatDate(endDate, 'dd');
      const endMonthDay = formatDate(endDate, 'MMMdd');
      const endYear = formatDate(endDate, 'yyyy');

      const sameMonth = isSameMonth(startDateObj, endDateObj);
      const sameYear = isSameYear(startDateObj, endDateObj);

      // Same month and year: "Jan 15 - 20, 2025"
      if (sameMonth && sameYear) {
        return `${startMonthDay} - ${endDay}, ${startYear}`;
      }

      // Different months, same year: "Jan 15 - Feb 20, 2025"
      if (!sameMonth && sameYear) {
        return `${startMonthDay} - ${endMonthDay}, ${startYear}`;
      }

      // Different years: "Dec 20, 2024 - Jan 5, 2025"
      return `${startMonthDay}, ${startYear} - ${endMonthDay}, ${endYear}`;
    },
    [formatDate, formatTime, tq],
  );

  const formatEventDateRange = useCallback(
    (startDate: NullableDateInput, endDate: NullableDateInput, format: DateRangeFormat, options?: FormatDateOptions) =>
      formatDateRangeForLanguage(language, startDate, endDate, format, options),
    [language],
  );

  return useMemo(
    () => ({
      formatDate,
      formatTime,
      formatDateTime,
      formatDateRange,
      formatEventDateRange,
      language,
    }),
    [formatDate, formatTime, formatDateTime, formatDateRange, formatEventDateRange, language],
  );
};
